/**
 * Query keys for the central audit viewer (docs/AUDIT.md §10a).
 *
 * Every audit read sits under `['audit', orgId, …]`, so a document or ledger
 * mutation refreshes whatever history is open with ONE prefix invalidation
 * (`invalidateAuditCaches`). Kept in `lib/` rather than `hooks/useAudit.ts`
 * so the document mutation hooks can import it without pulling in the read
 * hooks.
 */
import type { QueryClient } from '@tanstack/react-query';
import type { AuditActivityRange, AuditHistoryRecordType } from '@releve/shared';

/** Server-side filters of the org-wide log (all optional; `undefined` = no filter). */
export interface AuditEventsFilters {
  /** Registry keys (`expenses_audit`, …) — a row matches when it touches ANY of them. */
  sources?: readonly string[];
  /** One verb (`DELETE`, …) — matched against each set's `primary_action`. */
  action?: string;
  userId?: string;
  includeBaseline?: boolean;
  /** Inclusive calendar days (`YYYY-MM-DD`), read by the server in the org's timezone. */
  from?: string;
  to?: string;
  /**
   * One record, `<root_type>:<root_id>` (the view's `root_keys` entry). Sent
   * to the API as the `rootType` + `rootId` pair.
   */
  rootKey?: string;
}

/** The filters the activity chart shares with the log — every one but the date window. */
export type AuditActivityFilters = Omit<AuditEventsFilters, 'from' | 'to'>;

function normalizeFilters(filters: AuditActivityFilters) {
  return {
    sources: filters.sources && filters.sources.length > 0 ? [...filters.sources].sort() : null,
    action: filters.action ?? null,
    userId: filters.userId ?? null,
    includeBaseline: filters.includeBaseline ?? false,
    rootKey: filters.rootKey ?? null,
  };
}

export const auditKeys = {
  org: (orgId: string | undefined) => ['audit', orgId ?? ''] as const,
  /** `orgId` undefined → the `''` slot: a platform record's History (no org). */
  record: (orgId: string | undefined, type: AuditHistoryRecordType, id: string | undefined) =>
    ['audit', orgId ?? '', 'record', type, id ?? ''] as const,
  events: (orgId: string | undefined, filters: AuditEventsFilters) =>
    [
      'audit',
      orgId ?? '',
      'events',
      { ...normalizeFilters(filters), from: filters.from ?? null, to: filters.to ?? null },
    ] as const,
  /** The chart: every day of the window, the log's filters minus the dates. */
  activity: (orgId: string | undefined, range: AuditActivityRange, filters: AuditActivityFilters) =>
    ['audit', orgId ?? '', 'activity', { range, ...normalizeFilters(filters) }] as const,
  /** "Find a record…": one entry per (trimmed) search term. */
  search: (orgId: string | undefined, q: string) => ['audit', orgId ?? '', 'search', q] as const,
  /** The newest non-baseline change set — the day the page opens on. */
  latest: (orgId: string | undefined) => ['audit', orgId ?? '', 'latest'] as const,
  changeSet: (orgId: string | undefined, timestamp: string, key: string) =>
    ['audit', orgId ?? '', 'change-set', timestamp, key] as const,
};

/**
 * Mark every audit read of the org stale. Only mounted queries refetch (the
 * History disclosures mount on open), so this is cheap to call from every
 * document / ledger mutation.
 */
export function invalidateAuditCaches(queryClient: QueryClient, organizationId: string | undefined): void {
  if (!organizationId) return;
  void queryClient.invalidateQueries({ queryKey: auditKeys.org(organizationId) });
}
